import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { UserIcon, CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { Student } from '../../hooks/useStudentsList';
import { AttendanceRecord } from '../../services/attendanceService';

// 脱敏工具函数
const maskSensitiveInfo = {
  // 邮箱脱敏：p*****@gmail.com
  email: (email: string): string => {
    if (!email) return '';
    const [local, domain] = email.split('@');
    if (local.length <= 1) return `${local}*****@${domain}`;
    return `${local.charAt(0)}${'*'.repeat(Math.max(1, local.length - 1))}@${domain}`;
  },

  // 电话脱敏：只显示前3位和后4位，其余用*替代
  phone: (phone: string): string => {
    if (!phone) return '';
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length <= 7) return '*'.repeat(cleanPhone.length);
    return `${cleanPhone.substring(0, 3)}${'*'.repeat(cleanPhone.length - 7)}${cleanPhone.substring(cleanPhone.length - 4)}`;
  },

  // 姓名脱敏：显示首字母，其余用*替代
  name: (name: string): string => {
    if (!name || name.length <= 1) return name;
    return `${name.charAt(0)}${'*'.repeat(name.length - 1)}`;
  }
};

interface StudentAttendanceCardProps {
  student: Student;
  attendanceRecord?: AttendanceRecord;
  onSignIn: (studentId: string) => void;
  onSignOut: (studentId: string) => void;
  isAttendanceEnabled: boolean;
}

const StudentAttendanceCard: React.FC<StudentAttendanceCardProps> = ({
  student,
  attendanceRecord,
  onSignIn,
  onSignOut,
  isAttendanceEnabled
}) => {
  const { t } = useTranslation();

  // 检查是否已签到或签退
  const hasSignedIn = Boolean(attendanceRecord?.signInTime);
  const hasSignedOut = Boolean(attendanceRecord?.signOutTime);

  // 格式化时间
  const formatTime = (date: Date | null): string => {
    if (!date) return '';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // 点击卡片触发的操作
  const handleCardClick = () => {
    if (!isAttendanceEnabled) return;
    
    if (!hasSignedIn) {
      onSignIn(student.id);
    } else if (!hasSignedOut) {
      onSignOut(student.id);
    }
  };

  // 获取卡片样式类
  const getCardClass = () => {
    let baseClass = "bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden shadow-lg border relative ";
    
    if (!isAttendanceEnabled) {
      return baseClass + "border-white/10 opacity-70";
    }
    
    if (hasSignedOut) {
      return baseClass + "border-green-400/30";
    }
    
    if (hasSignedIn) {
      return baseClass + "border-yellow-400/30";
    }
    
    return baseClass + "border-white/10 cursor-pointer hover:border-blue-400/30";
  };

  return (
    <motion.div
      className={getCardClass()}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      onClick={handleCardClick}
    >
      {/* 签到/签退状态标签 */}
      {hasSignedIn && (
        <div className={`absolute top-2 right-2 flex items-center rounded-full px-2 py-1 text-xs ${
          hasSignedOut ? 'bg-green-500/20 text-green-200' : 'bg-yellow-500/20 text-yellow-200'
        }`}>
          <CheckCircleIcon className="h-3 w-3 mr-1" />
          {hasSignedOut ? t('attendance.signedOut') : t('attendance.signedIn')}
        </div>
      )}

      <div className="p-6">
        <div className="flex items-center mb-4">
          {student.avatar ? (
            <img
              src={student.avatar}
              alt={student.firstName}
              className="w-16 h-16 rounded-full object-cover mr-4 border-2 border-blue-300"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-blue-700 flex items-center justify-center mr-4 border-2 border-blue-300">
              <UserIcon className="h-8 w-8 text-blue-200" />
            </div>
          )}
          <div>
            <div className="flex flex-col">
              <h3 className="text-lg font-semibold text-white">
                {student.firstName} {student.lastName}
                {student.chineseName && ` (${student.chineseName})`}
              </h3>
              <div className="flex flex-wrap gap-2 mt-1">
                {/* {student.gender && (
                  <span className="text-sm text-blue-200">
                    {student.gender === 'male' ? t('student.male') : 
                     student.gender === 'female' ? t('student.female') : 
                     t('student.other')}
                  </span>
                )}
                {student.age > 0 && (
                  <span className="text-sm text-blue-200">
                    {student.age} {t('student.yearsOld')}
                  </span>
                )} */}
              </div>
            </div>
          </div>
        </div>

        {/* 考勤信息 */}
        {(hasSignedIn || hasSignedOut) && (
          <div className="mt-4 pt-4 border-t border-blue-700/30">
            <h4 className="text-sm font-medium text-blue-100 mb-2">{t('attendance.status')}</h4>
            
            {hasSignedIn && (
              <div className="flex justify-between text-sm text-blue-200 mb-1">
                <span>{t('attendance.signInTime')}:</span>
                <span>{formatTime(attendanceRecord?.signInTime || null)}</span>
              </div>
            )}
            
            {hasSignedIn && (
              <div className="flex justify-between text-sm text-blue-200 mb-1">
                <span>{t('attendance.signInBy')}:</span>
                <span>{attendanceRecord?.signInName}</span>
              </div>
            )}
            
            {hasSignedOut && (
              <div className="flex justify-between text-sm text-blue-200 mb-1">
                <span>{t('attendance.signOutTime')}:</span>
                <span>{formatTime(attendanceRecord?.signOutTime || null)}</span>
              </div>
            )}
            
            {hasSignedOut && (
              <div className="flex justify-between text-sm text-blue-200">
                <span>{t('attendance.signOutBy')}:</span>
                <span>{attendanceRecord?.signOutName}</span>
              </div>
            )}
          </div>
        )}

        {/* 操作提示 - 更加醒目的设计 */}
        {isAttendanceEnabled && !hasSignedOut && (
          <div className="mt-4 pt-4 border-t border-blue-700/30">
            <button 
              className={`w-full py-2 px-4 rounded-lg flex items-center justify-center gap-2 text-sm font-medium transition-colors ${
                !hasSignedIn 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                  : 'bg-yellow-600 hover:bg-yellow-700 text-white'
              }`}
              onClick={handleCardClick}
            >
              {!hasSignedIn ? (
                <>
                  <CheckCircleIcon className="h-4 w-4" />
                  {t('attendance.clickToSignIn')}
                </>
              ) : (
                <>
                  <ArrowRightIcon className="h-4 w-4" />
                  {t('attendance.clickToSignOut')}
                </>
              )}
            </button>
          </div>
        )}

        {/* 家长信息 */}
        {/* {(student.parentFirstName || student.parentLastName || student.parentPhone) && (
          <div className="mt-4 pt-4 border-t border-blue-700/30">
            <h4 className="text-sm font-medium text-blue-100 mb-2">{t('student.parentInfo')}</h4>
            {(student.parentFirstName || student.parentLastName) && (
              <p className="text-sm text-blue-200 mb-1">
                <span className="opacity-70">{t('student.parentName')}:</span> {maskSensitiveInfo.name(student.parentFirstName || '')} {maskSensitiveInfo.name(student.parentLastName || '')}
              </p>
            )}
            {student.parentPhone && (
              <p className="text-sm text-blue-200 mb-1">
                <span className="opacity-70">{t('student.parentPhone')}:</span> {maskSensitiveInfo.phone(student.parentPhone)}
              </p>
            )}
            {student.email && (
              <p className="text-sm text-blue-200">
                <span className="opacity-70">{t('contact.info.email.title')}:</span> {maskSensitiveInfo.email(student.email)}
              </p>
            )}
          </div>
        )} */}
      </div>
    </motion.div>
  );
};

export default StudentAttendanceCard; 